import {hpcAttrs} from '@github-ui/hpc'
import {FilteredListPageLayout} from '@github-ui/layout-primitives/FilteredListPageLayout'
import {CollapsibleNavList} from '@github-ui/layout-primitives/CollapsibleNavList'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ClockIcon, GitBranchIcon, HistoryIcon, HomeIcon, PersonIcon, type Icon} from '@primer/octicons-react'
import {Heading, PageLayout, UnderlineNav} from '@primer/react'
import {Link} from '@github-ui/react-router'
import {CreateBranchButton} from '@github-ui/create-branch-button-ui'
import type {PropsWithChildren} from 'react'
import {useCreateBranchButtonOptions} from '../contexts/CreateBranchButtonOptionContext'
import type {BranchPageType} from '../types'
import {BRANCH_PAGES} from '../constants'
import SearchInput from './SearchInput'
import BranchPagination from './BranchPagination'
import useBranchTabPath from '../hooks/use-branch-tab-path'
import {useCurrentRepository} from '@github-ui/current-repository'

import styles from './Layout.module.css'

const BRANCH_PAGE_ICONS: Record<BranchPageType, Icon> = {
  overview: HomeIcon,
  yours: PersonIcon,
  active: ClockIcon,
  stale: HistoryIcon,
  all: GitBranchIcon,
}

const BRANCH_PAGE_TITLE = {
  overview: 'Branches',
  yours: 'Your branches',
  active: 'Active branches',
  stale: 'Stale branches',
  all: 'All branches',
}

export default function Layout({
  selectedPage,
  children,
}: PropsWithChildren<{
  selectedPage: BranchPageType
}>) {
  const {currentUserCanPush} = useCurrentRepository()
  const getBranchTabPath = useBranchTabPath()
  const createButtonOptions = useCreateBranchButtonOptions()
  const sidebarLayoutEnabled = isFeatureEnabled('branches_sidebar_layout')
  const visiblePages = BRANCH_PAGES.filter(({onlyShowForPushUsers}) => currentUserCanPush || !onlyShowForPushUsers)
  const overviewTitle = sidebarLayoutEnabled ? 'Branches' : 'Overview'
  const title = sidebarLayoutEnabled ? BRANCH_PAGE_TITLE[selectedPage] : 'Branches'

  if (!sidebarLayoutEnabled) {
    return (
      <PageLayout>
        <PageLayout.Header>
          <div className={styles.HeaderContainer}>
            <Heading as="h1" className={styles.Heading}>
              {title}
            </Heading>
            <div className={styles.HeaderActions}>
              {currentUserCanPush && <CreateBranchButton {...createButtonOptions} liveReload />}
            </div>
          </div>
        </PageLayout.Header>
        <PageLayout.Content as="div">
          <div className={styles.ContentContainer}>
            <UnderlineNav aria-label="Branches">
              {visiblePages.map(page => {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const tabPath = getBranchTabPath(page.type)!
                return (
                  <UnderlineNav.Item
                    key={page.type}
                    as={Link}
                    to={tabPath}
                    aria-current={page.type === selectedPage ? 'page' : undefined}
                  >
                    {page.type === 'overview' ? overviewTitle : page.title}
                  </UnderlineNav.Item>
                )
              })}
            </UnderlineNav>

            <SearchInput selectedPage={selectedPage} />

            <div {...hpcAttrs()}>{children}</div>

            {selectedPage !== 'overview' ? <BranchPagination /> : null}
          </div>
        </PageLayout.Content>
      </PageLayout>
    )
  }

  return (
    <FilteredListPageLayout storageKey="repos-branches">
      <FilteredListPageLayout.Header>
        <FilteredListPageLayout.PageHeader>
          <FilteredListPageLayout.PageHeader.TitleArea>
            <FilteredListPageLayout.PageHeader.Title as="h1">{title}</FilteredListPageLayout.PageHeader.Title>
          </FilteredListPageLayout.PageHeader.TitleArea>
          <FilteredListPageLayout.PageHeader.Actions>
            {currentUserCanPush && <CreateBranchButton {...createButtonOptions} liveReload />}
          </FilteredListPageLayout.PageHeader.Actions>
        </FilteredListPageLayout.PageHeader>
      </FilteredListPageLayout.Header>
      <FilteredListPageLayout.Sidebar aria-label="Branch views" dialogTitle="Branch views">
        <CollapsibleNavList aria-label="Branch views">
          {visiblePages.map(page => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const pagePath = getBranchTabPath(page.type)!
            return (
              <CollapsibleNavList.LinkItem
                key={page.type}
                to={pagePath}
                label={page.type === 'overview' ? overviewTitle : page.title}
                icon={BRANCH_PAGE_ICONS[page.type]}
                aria-current={page.type === selectedPage ? 'page' : undefined}
              />
            )
          })}
        </CollapsibleNavList>
      </FilteredListPageLayout.Sidebar>
      <FilteredListPageLayout.Content as="div">
        <FilteredListPageLayout.FilterBar aria-label="Search branches">
          <SearchInput selectedPage={selectedPage} />
        </FilteredListPageLayout.FilterBar>

        <FilteredListPageLayout.Results aria-label="Branches" {...hpcAttrs()}>
          {children}
        </FilteredListPageLayout.Results>

        {selectedPage !== 'overview' ? <BranchPagination /> : null}
      </FilteredListPageLayout.Content>
    </FilteredListPageLayout>
  )
}
